import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { RootState } from '../../../reducers';
import { fetchFiles, clearFiles, hydrateFilesFromCache } from '../librarySlice';
import { RouteParams } from '../types';
import FilesEmpty from './FilesEmpty';
import FileGridView from './gridView';
import FileListView from './listView';
import { useQuery } from '../../../hooks/urlHooks';
import { useUserTags } from '../../../hooks/useTags';
import { SortBy, SortOrderBy } from '../../../types';
import {
  loadFilesFromCache,
  saveFilesToCache,
} from '../../../lib/libraryStateCache';

export type viewType = 'grid' | 'list';

export const Files = () => {
  const { library, upload } = useSelector((state: RootState) => state);
  const { activeUploads } = upload;
  const dispatch = useDispatch();
  const { teamId, folderId } = useParams<RouteParams>();
  const query = useQuery();
  const { getTag } = useUserTags();
  const { searchQuery } = query;
  const sortBy = (getTag('library-sort-by') as SortBy) || 'createdAt';
  const sortOrder = (getTag('library-sort-order') as SortOrderBy) || 'desc';
  const { files, lastRefreshTriggered } = library;
  const { status, isFromCache } = files;

  // Track previous values for cache saving
  const prevTeamFolderRef = useRef<{ teamId?: string; folderId?: string }>({});

  // Get view from URL or fallback to saved preference
  const viewFromUrl = query.view as viewType | undefined;
  const savedView = getTag('library-view-type') as viewType | null;
  const view = viewFromUrl || savedView || 'grid';

  // Refetch logic
  const refetchFiles = () => {
    dispatch(
      fetchFiles({
        teamId,
        folderId,
        page: 1,
        limit: 20,
        searchQuery,
        sortBy,
        sortOrder,
      }),
    );
  };

  // Initial fetch + refetch on dependency change
  // Note: 'view' is intentionally excluded - switching views shouldn't refetch data
  useEffect(() => {
    // Save current files to cache before clearing (if we have fresh data)
    const prev = prevTeamFolderRef.current;
    if (
      prev.teamId &&
      prev.folderId &&
      files.data.length > 0 &&
      !isFromCache &&
      status === 'succeeded'
    ) {
      saveFilesToCache(prev.teamId, prev.folderId, {
        files: files.data,
        pagination: files.pagination,
        librarySize: files.librarySize,
      });
    }

    // Update ref for next navigation
    prevTeamFolderRef.current = { teamId, folderId };

    // Clear old files first
    dispatch(clearFiles());

    // Try to load from cache first for instant display
    if (teamId && folderId && !searchQuery) {
      const cached = loadFilesFromCache(teamId, folderId);
      if (cached) {
        dispatch(
          hydrateFilesFromCache({
            files: cached.files,
            pagination: cached.pagination,
            librarySize: cached.librarySize,
          }),
        );
      }
    }

    // Always fetch fresh data (will update cache data when it arrives)
    refetchFiles();
  }, [teamId, folderId, searchQuery, sortBy, sortOrder, dispatch]);

  // Save to cache when fresh data arrives
  useEffect(() => {
    if (
      teamId &&
      folderId &&
      files.data.length > 0 &&
      !isFromCache &&
      status === 'succeeded' &&
      !searchQuery
    ) {
      saveFilesToCache(teamId, folderId, {
        files: files.data,
        pagination: files.pagination,
        librarySize: files.librarySize,
      });
    }
  }, [
    teamId,
    folderId,
    files.data,
    files.pagination,
    files.librarySize,
    isFromCache,
    status,
    searchQuery,
  ]);

  // Refetch when the refresh timestamp changes (from socket)
  useEffect(() => {
    if (lastRefreshTriggered > 0) {
      // Check if timestamp is valid
      refetchFiles();
    }
  }, [lastRefreshTriggered]); // Add the timestamp as a dependency

  if (!teamId || !folderId) {
    return null;
  }

  // Don't show empty state while loading - let the view components handle skeleton states
  if (
    status === 'succeeded' &&
    files.data?.length === 0 &&
    activeUploads.length === 0
  ) {
    return (
      <div className="px-4 pb-4 relative">
        <FilesEmpty />
      </div>
    );
  }

  // Show view components which will handle loading states with skeletons
  // List view is edge-to-edge (Twitter-like), grid view keeps padding
  return (
    <div className={view === 'list' ? 'relative' : 'px-4 pb-4 relative'}>
      {view === 'list' ? <FileListView /> : <FileGridView />}
    </div>
  );
};

export default Files;
